// Single source of truth for the runtime keys an animation asset is registered
// under in the mixer / FaceClipPlayer.
//
// A clip MUST resolve by BOTH:
//   1. its human asset name — what PlayClipNode's dropdown stores and what
//      blueprint references use, and
//   2. its `custom_<kind>_<id>` alias — what prefab / ID-based resolution uses.
//
// Registering under only one of these was the ENG-676 bug: a freshly-imported
// face clip worked in its upload session (both keys registered) but lost the
// by-name key on the next reload (registerToRuntime registered only the alias),
// so a name-selected Play Clip node went unresolved and the face sat still.
//
// Returns an ordered list: keys[0] is the primary (the name when present),
// remaining entries are aliases. Callers fetch once and register under every
// key (see loadBodyClip/loadFaceClip `aliases`).
export function clipRuntimeKeys(asset) {
  const keys = []
  if (asset?.name) keys.push(asset.name)
  if (asset?.id != null) {
    const alias = `${asset.kind === 'face' ? 'custom_face_' : 'custom_body_'}${asset.id}`
    if (!keys.includes(alias)) keys.push(alias)
  }
  return keys
}

// The subset of an asset's runtime keys that aren't already registered.
//
// registerToRuntime() runs shortly after avatar-ready, when AvatarModel has
// already registered every animation_asset under its NAME (JIT loader, served
// from animCache). Re-registering an already-present key is actively harmful:
// __REGISTER_CUSTOM_BODY_CLIP__ force-REPLACES the live AnimationAction with a
// brand-new object, orphaning any reference held across frames (the root cause
// of the prefab-blend-envelope CI hang, 2026-07-06) and can visibly reset a
// clip mid-playback, all while re-downloading the GLB uncached.
//
// So callers register only the MISSING keys: on a fresh load the name is
// already present and only the `custom_<kind>_<id>` alias (needed by the Clips
// Test button / ID resolution) still has to be added; when nothing is missing
// the caller skips the asset — and its network fetch — entirely. `registered`
// is the live key→action map for the asset's kind (__AVATAR_ACTIONS__ for body,
// FaceClipPlayer's clip map for face). Mirrors the guard refresh() already uses.
export function unregisteredClipKeys(asset, registered) {
  const reg = registered ?? {}
  return clipRuntimeKeys(asset).filter(k => !reg[k])
}

// Decide HOW to give an asset its missing runtime keys — the ownership rule,
// separated from the I/O so it can be tested and so the rule is stated once.
//
// Every animation_asset has exactly one owner that loads it by NAME and, since
// this rule was written, registers its aliases too as the clip lands:
//   body → AvatarModel's BodyClipLoader,  face → FaceClipPlayer.loadAll.
// An alias (`custom_<kind>_<id>`) is the SAME bytes under a second name, so it
// never justifies a download. Three outcomes:
//
//   'skip'  — every key is registered, OR the owner has the clip queued and will
//             register the missing key itself when it gets there.
//   'alias' — the name is registered but an alias is not. Derive it from the
//             registered clip: synchronous, no I/O.
//   'load'  — nobody owns it (no loader running, or a row the owner never got).
//             This is the only outcome that may touch the network.
//
// Two bugs are encoded against here, and they pull in opposite directions:
//
//   • 'alias' used to be 'load'. registerToRuntime assumed the owner had
//     finished, which held when readiness meant "all clips loaded" and stopped
//     being true when it became "rig + PRIORITY clips" with the rest on a
//     background sweep. So it re-downloaded and re-parsed every row — measured
//     on myra, 58 uncached GLB downloads and 116 parse+prune passes on the main
//     thread, racing the very sweep fetching the same files.
//   • The first fix overshot: it treated "owner has it queued" as 'alias' and
//     awaited the owner via `ensure()`. But `ensure()` deliberately bypasses the
//     loader's pause(), so asking for all 58 at once converted the paced sweep
//     into a burst and defeated the pacing that keeps the main thread
//     responsive. An alias is never urgent. Queued ⇒ 'skip'.
//
// @param {object} asset  the animation_asset row.
// @param {object} opts
// @param {object} opts.registered  live key→value map for the asset's kind.
// @param {boolean} opts.owned      does the kind's owner have, or claim, the name?
// @returns {{action: 'skip'|'alias'|'load', source?: string, primary?: string, aliases: string[]}}
export function planClipRegistration(asset, { registered, owned = false } = {}) {
  const missing = unregisteredClipKeys(asset, registered)
  if (!missing.length) return { action: 'skip', aliases: [] }

  const name = asset?.name ?? null
  const aliases = missing.filter(k => k !== name)
  // `owned` is only meaningful WITH a name — the owners key everything by name,
  // so an unnamed row can never be theirs, whatever a caller passes.
  if (name && !missing.includes(name)) return { action: 'alias', source: name, aliases }
  if (name && owned) return { action: 'skip', aliases: [] }

  const [primary, ...rest] = missing
  return { action: 'load', primary, aliases: rest }
}
